import { cn } from '@/lib/utils';

interface GapSignCellProps {
  /** Raw numeric value (BigDecimal arrives as string from API) */
  value: string | number;
  /** Unit suffix, e.g. "MMAD" or "%" */
  unit?: string;
  className?: string;
}

/**
 * Table cell that formats a gap amount with sign colouring:
 *   positive → cx-success green with "+" prefix
 *   negative → cx-danger red
 *   zero     → muted
 *
 * NOTE: values arrive from the API as strings (BigDecimal).
 * Never pass a JS float — always pass the raw string from the envelope.
 */
export function GapSignCell({ value, unit, className }: GapSignCellProps) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const isPositive = num > 0;
  const isNegative = num < 0;

  const formatted = new Intl.NumberFormat('fr-MA', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(num));

  return (
    <span
      className={cn(
        'font-mono text-[12px]',
        isPositive && 'text-cx-success',
        isNegative && 'text-cx-danger',
        !isPositive && !isNegative && 'text-muted-foreground',
        className,
      )}
    >
      {isPositive ? '+' : isNegative ? '−' : ''}
      {formatted}
      {unit ? ` ${unit}` : ''}
    </span>
  );
}
